import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { CheckableLineItem, LineDamage, LineItemStatus, lineKey } from '../../../service/receiving-mobile/log-entry-draft.store';
import { ReceivingLogReceipt } from '../../../models/poData/ReceivingStatus';
import { isRaggedLine } from '../../../domain/receiving-log/rules/line-receipts.rules';

/**
 * line-item-checkoff.component.ts
 *
 * The PO line-item check-off: a collapsible list of PO lines, each marked Arrived
 * or Partial, with a quantity for a countable partial and a parts list for a
 * ragged one. Extracted from `items-step.component.ts` so the receiving-log EDIT
 * screen can present the same control the delivery was LOGGED with, rather than a
 * second dialect of the same idea.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT OWNS, AND WHAT IT DELIBERATELY DOES NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It owns the ROWS and the interactions ON the rows: expand/collapse, the header
 * pills, the status buttons, the quantity and parts inputs, and the read-only
 * prior-receipts list. It is a pure input/output component — no store, no HTTP, no
 * knowledge of which screen it is on.
 *
 * It owns NONE of the following, all of which stay with the host:
 *
 * - **Where the rows came from.** The mobile step builds them from po-data through
 *   the draft store; the edit screen seeds them from the log's own receipt rows
 *   (`log-edit-line-items.rules.ts`). Neither is this component's business.
 * - **Damage EDITING.** The badge is here because it belongs in the header next to
 *   the arrival pill, but the panel behind it is ~250 lines of photo capture,
 *   camera and file services, stepper and modal state — and the edit screen does
 *   not edit damage at all (sending `damagedQuantity` makes the backend re-derive
 *   `isDamaged`, so a 0 erases a report whose photos stay attached). The host
 *   supplies it through {@link linePanel}, so those services stay out of the bundle
 *   of the screen that does not use them.
 * - **What is still MISSING.** Mobile reconciles that on its own step; the edit
 *   screen keeps it as text the user manages. Nothing about it is shared, by
 *   decision — a shortfall is not a property of a line's arrival.
 * - **Gating and navigation.** Whether an unmarked open line blocks Continue, and
 *   the PO-complete suggestion, are the host's rules. This component reports
 *   expansion through {@link expandedChange} so the host can apply them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SELF-STYLED, ON PURPOSE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every class is `lic-`-prefixed and every rule ships with the component. The
 * mobile step's styling reached these rows two ways — its own stylesheet, and
 * theme rules under a `.receiving-mobile` ancestor (`.partial-parts-input`) that
 * the log table has not got. Leaving either in place would mean the same component
 * looking different in its two homes, and the theme selector and a component
 * selector would land on the same specificity and be settled by load order. So the
 * rules were moved here and de-scoped. Same precedent as `ContainerChipsComponent`
 * and `ItemReprintListComponent`.
 */
@Component({
  selector: 'app-line-item-checkoff',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './line-item-checkoff.component.html',
  styleUrls: ['./line-item-checkoff.component.css'],
})
export class LineItemCheckoffComponent {

  // #region Inputs / outputs

  /**
   * The lines to render, PO lines and off-PO adds together.
   *
   * Mutated by REPLACEMENT: every handler emits a new array through
   * {@link rowsChange} rather than writing into this one, so a host holding these
   * in a store sees a change it can react to.
   */
  @Input() rows: CheckableLineItem[] = [];

  /** Per-line damage, keyed by `lineKey`. Read here for the badge only — never written. */
  @Input() damageByLine: Record<string, LineDamage> = {};

  /**
   * A pre-feature PO, whose tracked totals cannot be trusted because earlier
   * deliveries are not in the ledger. Only changes the closed pill's wording, from
   * "Closed" to "Received (tracked)" — the distinction the user needs is that the
   * number is a tracked number, not the whole truth.
   */
  @Input() isLegacyPo = false;

  /**
   * Offers a third action, "Not on this delivery", that clears a line's status.
   *
   * OFF for the mobile submit, where an unmarked line simply writes no receipt and
   * "Cancel" (collapse) is the whole gesture. ON for the edit screen, where
   * clearing an arrival is a real edit that takes the line's receipt rows off, and
   * therefore has to be something the user can say rather than imply.
   */
  @Input() allowClearStatus = false;

  /*
   * There is deliberately NO "nothing more is coming on this line" tick.
   *
   * `completesLine` is DERIVED, and was long before this control existed. The
   * submit's own builder decides it per line and this component feeds that same
   * builder, so both consumers get the same answer without asking the user twice:
   *
   *   - Arrived  → `completesLine: true`, always (`buildCompleteRow`). Marking a
   *     line arrived IS the statement that it is done.
   *   - Partial  → `!!row.completesLine`, i.e. whatever the line already carried.
   *     A partial does not close a line, and an edit must not silently start
   *     closing one.
   *   - refused / returned damage → cleared by `applyDamage`. The goods did not
   *     stay, so the line is outstanding again whatever the tap said.
   *   - status cleared → the line's receipt rows come off entirely, so its closing
   *     tap goes with them and the PO line reopens on its own.
   *
   * A checkbox on top of that would be a second, disagreeing opinion about the same
   * fact — and on a legacy PO the honest position is exactly what the derivation
   * already gives: some lines cannot be fully resolved, the legacy notice says so,
   * and nothing here should ask the user to certify otherwise.
   */

  /**
   * Renders an off-PO row's arrival quantity as an editable field.
   *
   * OFF for the mobile submit, which manages an off-PO item through its own
   * Edit/Remove buttons in {@link linePanel} — the item was typed in on that screen,
   * so its description and quantity are edited together in a form. ON for the edit
   * screen, where the description is stored history that is not ours to rewrite and
   * the quantity is the only part the user may correct.
   */
  @Input() allowOffPoQtyEdit = false;

  /**
   * Offers a Remove on an off-PO row.
   *
   * OFF for the mobile submit, which has its own Remove in {@link linePanel} — it
   * also has to drop any damage flagged on the row, which is this component's
   * business to trigger and the host's to carry out. ON for the edit screen, where
   * removing the row means DELETING its receipt from the ledger.
   */
  @Input() allowOffPoRemove = false;

  /**
   * Renders a line as read-only. The host decides, because the reasons differ:
   * mobile locks lines already closed on the PO, while the edit screen has to keep
   * a line THIS log received against editable even when it was closed — otherwise
   * the arrival it exists to fix is the one thing it cannot reach.
   *
   * Canceled lines are read-only regardless; that is not a host judgment.
   */
  @Input() isLineReadOnly: (row: CheckableLineItem) => boolean = () => false;

  /**
   * A note shown in place of the controls on a read-only line, so a locked row says
   * WHY rather than just refusing to respond.
   */
  @Input() readOnlyNote: (row: CheckableLineItem) => string = () => '';

  /**
   * Host markup rendered inside each expanded line, below the controls — the
   * mobile step's damage panel. Receives `{ $implicit: row, index }`.
   *
   * A template rather than `<ng-content>` because there is one per ROW, and content
   * projection cannot repeat inside an `*ngFor`.
   */
  @Input() linePanel: TemplateRef<unknown> | null = null;

  /** An inline "finish this line" message on one row, by index. `null` shows none. */
  @Input() errorAtIndex: number | null = null;
  @Input() errorText = '';

  /*
   * There is deliberately no `compact` input.
   *
   * One was added so these rows would sit more quietly inside the log-edit screen's
   * card stack — hairline separators, no status tint, tighter controls. It made the
   * page HARDER to read, which is the opposite of the point: the tint is what lets a
   * user find the line they marked without reading every row, and shrinking the
   * controls cost legibility to buy tidiness nobody asked for.
   *
   * These rows now look the same in both homes, which is also the easier thing to
   * defend: a user correcting a delivery is looking at the screen they recorded it
   * on. Host-side density belongs in the host's own card padding, not in a second
   * rendering of this component.
   */

  /** The rows after an edit. Always a NEW array. */
  @Output() rowsChange = new EventEmitter<CheckableLineItem[]>();

  /**
   * A line whose status was just turned OFF.
   *
   * Separate from {@link rowsChange} because the host has cleanup this component
   * cannot do: mobile drops the line's damage report, which would otherwise be left
   * as an orphan — an invisible incomplete report that blocks Continue, or a photo
   * with no receipt to attach to.
   */
  @Output() statusCleared = new EventEmitter<CheckableLineItem>();

  /**
   * An off-PO row the user removed, reported ALONGSIDE the new rows.
   *
   * Separate from {@link rowsChange} for the same reason {@link statusCleared} is:
   * the host may have cleanup this component cannot do — mobile drops the row's
   * damage report, and the edit screen has to notice the removal in order to delete
   * the receipt behind it.
   */
  @Output() rowRemoved = new EventEmitter<CheckableLineItem>();

  /** Which row indices are expanded, for host gating (see the class doc). */
  @Output() expandedChange = new EventEmitter<number[]>();

  // #endregion

  // #region Expansion

  private _expanded = new Set<number>();

  /**
   * Opens the given row indices and closes the rest.
   *
   * Called by a host that wants a particular starting state — the edit screen opens
   * the lines this delivery actually touched, since those are why the user is
   * there, whereas the submit starts with everything closed because nothing has
   * been acted on yet.
   */
  public setExpanded(indices: number[]): void {
    this._expanded = new Set(indices);
  }

  /**
   * Declarative form of {@link setExpanded}, for a host that computes the starting
   * state from its own data.
   *
   * `null` leaves expansion alone, which is the submit's case: nothing has been
   * acted on yet, so every row starts closed. The edit screen passes the rows its
   * delivery actually touched — those are why the user opened the screen.
   */
  @Input() set expandedIndices(indices: number[] | null) {
    if (indices) { this.setExpanded(indices); }
  }

  public isExpanded(index: number): boolean {
    return this._expanded.has(index);
  }

  public toggle(index: number): void {
    if (this.isCanceled(this.rows[index])) { return; }
    if (this._expanded.has(index)) {
      this._expanded.delete(index);
    } else {
      this._expanded.add(index);
    }
    this.expandedChange.emit([...this._expanded]);
  }

  /**
   * Collapses an opened-but-unmarked line without marking it.
   *
   * Expanding a line reveals the Arrived/Partial choice, so a line left open and
   * unmarked reads as handled when it is not — this is the way out that does not
   * claim anything arrived.
   */
  public collapse(index: number): void {
    this._expanded.delete(index);
    this.expandedChange.emit([...this._expanded]);
  }

  // #endregion

  // #region Row state

  /**
   * Row identity for the `*ngFor`, keyed on the LINE rather than its position.
   *
   * Every edit here replaces the row object (see {@link patch}), so with Angular's
   * default identity tracking the object at that index is one it has not seen
   * before and it tears down and rebuilds the row's DOM — including whichever input
   * the user is typing into. That is exactly what made "Arrived now" drop focus
   * after every character. `line` is unique across the whole set (PO lines are
   * positive and each off-PO add takes the next number BELOW the lowest one seen),
   * so it identifies a row without pinning it to an index that a removal shifts.
   */
  public trackRow(_index: number, row: CheckableLineItem): string {
    return lineKey(row);
  }

  public isCanceled(row: CheckableLineItem | undefined): boolean {
    return !!row?.isCanceled;
  }

  /** Canceled lines are never editable; beyond that the host decides. */
  public isReadOnly(row: CheckableLineItem): boolean {
    return this.isCanceled(row) || this.isLineReadOnly(row);
  }

  public isClosed(row: CheckableLineItem): boolean {
    return !!row.isClosed;
  }

  /** A ragged line: its receipts are part rows with no count, so parts are shown instead of an X/Y. */
  public isRagged(row: CheckableLineItem): boolean {
    return isRaggedLine(row.receipts);
  }

  public damageFor(row: CheckableLineItem): LineDamage | null {
    return this.damageByLine[lineKey(row)] ?? null;
  }

  private dispositionFor(row: CheckableLineItem): string | null {
    return this.damageFor(row)?.disposition ?? null;
  }

  /** True when the line's damaged goods were refused or returned — received, then sent back. */
  public isRefusedOrReturned(row: CheckableLineItem): boolean {
    const disposition = this.dispositionFor(row);
    return disposition === 'Refuse' || disposition === 'Return';
  }

  /**
   * The damage badge, shown ALONGSIDE the arrival pill rather than replacing it: a
   * refused delivery still arrived, and the Arrived/Partial tap is a real event that
   * survives the refusal.
   */
  public damageBadge(row: CheckableLineItem): string {
    const disposition = this.dispositionFor(row);
    if (disposition === 'Refuse') { return 'Refused'; }
    if (disposition === 'Return') { return 'Returned'; }
    return this.damageFor(row) ? 'Damaged' : '';
  }

  /** Compact status text for the collapsed row. Shows only what the user entered — never an inferred total. */
  public statusLabel(row: CheckableLineItem): string {
    if (row.status === 'complete') { return 'Arrived'; }
    if (row.status === 'partial') {
      const received = String(row.receivedQty ?? '').trim();
      const total = String(row.totalQty ?? '').trim();
      if (received && total) { return `Partial ${received}/${total}`; }
      if (received) { return `Partial · ${received}`; }
      return 'Partial';
    }
    return '';
  }

  /** Outstanding on a ledgered line: expected minus what the ledger already carries. `null` without a rollup. */
  public outstandingQty(row: CheckableLineItem): number | null {
    if (row.quantityExpected == null || row.quantityReceived == null) { return null; }
    return Math.max(row.quantityExpected - row.quantityReceived, 0);
  }

  /**
   * The collapsed row's quantity pill. A ragged line shows how many parts are in
   * rather than a misleading "0 / N" (its aggregate stays 0 by design).
   */
  public qtyDisplay(row: CheckableLineItem): string {
    if (this.isRagged(row)) {
      const count = this.priorReceipts(row).length;
      return count > 0 ? `${count} part${count === 1 ? '' : 's'} in` : '';
    }
    if (row.quantityExpected != null) {
      return `${row.quantityReceived ?? 0} / ${row.quantityExpected}`;
    }
    return row.quantity ? `×${row.quantity}${row.unit ? ' ' + row.unit : ''}` : '';
  }

  /** The line's own receipt rows — read-only, and a ragged line's only visible progress. */
  public priorReceipts(row: CheckableLineItem): ReceivingLogReceipt[] {
    return row.receipts ?? [];
  }

  /** One prior receipt's label: the part description on a null-quantity row, else the counted amount. */
  public receiptLabel(receipt: ReceivingLogReceipt): string {
    if (receipt.quantityReceived == null) {
      return receipt.itemDescription || receipt.notes || 'Part received';
    }
    const unit = receipt.unitOfMeasure ? ` ${receipt.unitOfMeasure}` : '';
    return `${receipt.quantityReceived}${unit} received`;
  }

  // #endregion

  // #region Editing

  /**
   * Marks a line Arrived or Partial, or clears it by tapping the active one again.
   *
   * Marking Partial pre-fills the read-only "of ___" from the OUTSTANDING amount
   * (expected − already received), not the full ordered quantity: a receipt records
   * what arrived in THIS delivery, not a cumulative total.
   *
   * The row stays open after a choice so the selection and any quantity stay visible.
   */
  public setStatus(index: number, requested: LineItemStatus): void {
    const row = this.rows[index];
    if (!row || this.isReadOnly(row)) { return; }

    const turningOff = row.status === requested;
    const status = turningOff ? null : requested;

    const receivedQty = turningOff ? '' : row.receivedQty;
    let totalQty = turningOff ? '' : row.totalQty;
    if (status === 'partial' && !totalQty) {
      const outstanding = this.outstandingQty(row);
      totalQty = outstanding != null ? String(outstanding) : (row.quantity || '');
    }

    const next = [...this.rows];
    next[index] = { ...row, status, receivedQty, totalQty };
    this.rows = next;
    this.rowsChange.emit(next);

    this._expanded.add(index);
    this.expandedChange.emit([...this._expanded]);

    // The host has cleanup this component cannot do — see `statusCleared`.
    if (turningOff) { this.statusCleared.emit(row); }
  }

  /** "Not on this delivery" — an explicit clear, offered only when the host allows it. */
  public clearStatus(index: number): void {
    const row = this.rows[index];
    if (!row || !row.status) { return; }
    this.setStatus(index, row.status);
  }

  /**
   * A Partial line is described one way OR the other — a count or a parts list,
   * never both, because the note and the submitted receipts read those two fields
   * with OPPOSITE precedence. Entering one clears the other, but only once the
   * edited field actually has a value, so emptying a field does not wipe its
   * counterpart.
   */
  public setReceivedQty(index: number, value: string | number | null): void {
    this.patch(index, () => {
      // COERCED, not assumed. Angular's number value accessor hands a `number` (or
      // null) out of a `type="number"` input, and calling .trim() on that throws
      // inside the change handler — which aborts the update before it is applied, so
      // the field looks dead and nothing on the screen moves. The inputs are
      // `type="text"` + inputmode for this reason, and this is the belt to that
      // braces: the row's `receivedQty` is a STRING by contract, because a blank has
      // to be distinguishable from a typed zero.
      const receivedQty = asText(value);
      return receivedQty.trim() ? { receivedQty, partsReceived: '' } : { receivedQty };
    });
  }

  public setParts(index: number, value: string | number | null): void {
    this.patch(index, () => {
      const partsReceived = asText(value);
      return partsReceived.trim() ? { partsReceived, receivedQty: '' } : { partsReceived };
    });
  }

  /** An off-PO row's arrival, which lives in `quantity` — off-PO rows have no expected amount. */
  public setOffPoQty(index: number, value: string | number | null): void {
    this.patch(index, () => ({ quantity: asText(value) }));
  }

  /**
   * Takes an off-PO row off the list.
   *
   * Only off-PO rows: a PO line is the PO's, not the user's, and "remove line 3"
   * would mean removing it from the purchase order.
   */
  public removeOffPoRow(index: number): void {
    const row = this.rows[index];
    if (!row?.isOffPo) { return; }

    const next = this.rows.filter((_, i) => i !== index);
    this.rows = next;
    this.rowsChange.emit(next);
    this.rowRemoved.emit(row);
  }

  /**
   * Applies a field edit by REPLACING the row and the array, never by writing into
   * either.
   *
   * The Missing step solves the same focus problem the opposite way — it keeps its
   * row objects alive so an open input is never rebuilt — but that only works
   * because nothing downstream of it watches for a new reference. Here the hosts
   * do: the mobile step and the edit screen both take {@link rowsChange} as the
   * signal that the draft moved. So the replacement stays, and {@link trackRow}
   * makes it survivable — no patch touches `line`, so the row keeps its identity
   * and Angular re-renders it in place instead of rebuilding it.
   */
  private patch(index: number, change: (row: CheckableLineItem) => Partial<CheckableLineItem>): void {
    const row = this.rows[index];
    if (!row || this.isReadOnly(row)) { return; }
    const next = [...this.rows];
    next[index] = { ...row, ...change(row) };
    this.rows = next;
    this.rowsChange.emit(next);
  }

  // #endregion
}

/**
 * A form value as TEXT.
 *
 * Angular's number value accessor turns a `type="number"` input's value into a
 * `number` (or `null` when emptied), and every quantity on `CheckableLineItem` is a
 * STRING by contract — because a blank field and a typed zero mean different things
 * and a number cannot express the difference. Coercing here means a template that
 * uses the wrong input type degrades to a harmless string instead of throwing inside
 * a change handler, which silently aborts the update and makes the field look dead.
 */
function asText(value: string | number | null | undefined): string {
  return value == null ? '' : String(value);
}
